// C2 Offline identity for the web: the app's copy of the four facts.
// offline_owner and terminal_rejection are persisted in the device index
// (C5); session_usable is derived from the live session (subject == owner,
// exp > now + 60 s); refresh_pending is in memory. useAuth feeds it; the
// scheduler, the stores and the UI read `mode`.
//
// No browser global at module scope.
import { useSyncExternalStore } from 'react'
import { IDENTITY_EMIT, MODE, type IdentityEmit, type IdentityMode } from './offline.codes'
import { accountOwner, allowedIn, identityMode, isAccountOwner, ownerSubject, tokenUsable, type IdentityAllowed, type IdentityFacts } from './identityMachine'
import { updateDeviceIndex, hasIndexedDb, type DeviceIndex } from './offlineStore'

export interface IdentitySnapshot {
  /** The device index has been read (until then nothing owner-bound is shown). */
  loaded: boolean
  offlineOwner: string | null
  sessionSubject: string | null
  tokenExpiryMs: number | null
  refreshPending: boolean
  terminalRejection: boolean
  mode: IdentityMode
  allowed: IdentityAllowed
}

let facts = {
  loaded: false,
  offlineOwner: null as string | null,
  sessionSubject: null as string | null,
  tokenExpiryMs: null as number | null,
  refreshPending: false,
  terminalRejection: false,
}
let snapshot: IdentitySnapshot = build()
let nearExpiryTimer: ReturnType<typeof setTimeout> | null = null
let now: () => number = () => Date.now()
const listeners = new Set<() => void>()
const emitListeners = new Set<(e: IdentityEmit) => void>()

function factsNow(): IdentityFacts {
  const subjectMatches = !!facts.sessionSubject && isAccountOwner(facts.offlineOwner) && accountOwner(facts.sessionSubject) === facts.offlineOwner
  return {
    offlineOwner: facts.offlineOwner,
    sessionUsable: subjectMatches && tokenUsable(facts.tokenExpiryMs, now()),
    refreshPending: facts.refreshPending,
    terminalRejection: facts.terminalRejection,
  }
}

function build(): IdentitySnapshot {
  const f = factsNow()
  const mode = identityMode(f)
  return { ...facts, mode, allowed: allowedIn(mode) }
}

function publish(): void {
  const before = snapshot.mode
  snapshot = build()
  armNearExpiry()
  for (const l of [...listeners]) l()
  if (snapshot.mode === MODE.active && (before === MODE.refreshPending || before === MODE.rejected)) emit(IDENTITY_EMIT.sessionRestored)
}

function emit(e: IdentityEmit): void {
  for (const l of [...emitListeners]) l(e)
}

/** token_near_expiry: the mode turns refresh_pending without any event; re-derive then. */
function armNearExpiry(): void {
  if (nearExpiryTimer) clearTimeout(nearExpiryTimer)
  nearExpiryTimer = null
  if (snapshot.mode !== MODE.active || facts.tokenExpiryMs === null) return
  const at = facts.tokenExpiryMs - 60_000
  const wait = Math.max(0, at - now()) + 50
  // Timers longer than ~24 days overflow; a token never lives that long.
  nearExpiryTimer = setTimeout(() => {
    nearExpiryTimer = null
    publish()
    if (snapshot.mode === MODE.refreshPending) emit(IDENTITY_EMIT.requestRefresh)
  }, Math.min(wait, 2 ** 31 - 1))
}

export function getIdentity(): IdentitySnapshot {
  return snapshot
}

export function subscribeIdentity(fn: () => void): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

/** session_restored, show_sign_in_again, request_refresh. */
export function onIdentityEmit(fn: (e: IdentityEmit) => void): () => void {
  emitListeners.add(fn)
  return () => { emitListeners.delete(fn) }
}

export function useIdentity(): IdentitySnapshot {
  return useSyncExternalStore(subscribeIdentity, getIdentity, getIdentity)
}

/** The persisted facts (boot, and after C3 rewrote the device index). */
export function setIdentityFromIndex(idx: DeviceIndex): void {
  facts = { ...facts, loaded: true, offlineOwner: idx.offline_owner, terminalRejection: idx.terminal_rejection }
  publish()
}

/** No device index on this browser (no IndexedDB): guest semantics, loaded. */
export function setIdentityUnavailable(): void {
  facts = { ...facts, loaded: true }
  publish()
}

/**
 * The live session changed (sign-in, restore, TOKEN_REFRESHED, gone).
 * `subject` is a PERMANENT account's user id (anonymous and signed out: null).
 */
export function identitySessionChanged(subject: string | null, expiresAtSec: number | null | undefined): void {
  facts = {
    ...facts,
    sessionSubject: subject ? subject.toLowerCase() : null,
    tokenExpiryMs: subject && typeof expiresAtSec === 'number' ? expiresAtSec * 1000 : null,
  }
  if (subject && facts.offlineOwner === accountOwner(subject) && tokenUsable(facts.tokenExpiryMs, now())) facts.refreshPending = false
  publish()
}

/** offline_owner moved (activation, C3): persisted by the caller, mirrored here. */
export function identityOwnerChanged(owner: string | null, terminalRejection = false): void {
  facts = { ...facts, loaded: true, offlineOwner: owner, terminalRejection }
  publish()
}

/** A transient refresh failure (offline, 5xx, 429, malformed): NOT a sign-out. */
export function identityRefreshTransient(): void {
  if (!isAccountOwner(facts.offlineOwner) || facts.terminalRejection) return
  facts = { ...facts, refreshPending: true }
  publish()
}

/** GoTrue definitively rejected the refresh token: read-only, data kept (persisted). */
export async function identityRefreshTerminal(): Promise<void> {
  if (!isAccountOwner(facts.offlineOwner)) return
  const owner = facts.offlineOwner
  facts = { ...facts, refreshPending: false, terminalRejection: true, tokenExpiryMs: null, sessionSubject: null }
  publish()
  emit(IDENTITY_EMIT.showSignInAgain)
  if (!hasIndexedDb()) return
  try {
    await updateDeviceIndex(idx => { if (idx.offline_owner === owner) idx.terminal_rejection = true })
  } catch {
    // In memory for this page; the next load re-derives it (the session is gone).
  }
}

/** The same account signed in again after a rejection: no C3. */
export async function identityClearRejection(): Promise<void> {
  if (!facts.terminalRejection) return
  const owner = facts.offlineOwner
  facts = { ...facts, terminalRejection: false }
  publish()
  if (!hasIndexedDb()) return
  try {
    await updateDeviceIndex(idx => { if (idx.offline_owner === owner) idx.terminal_rejection = false })
  } catch {
    // Re-derived at the next load.
  }
}

/** The account that owns the device data, as a user id (null: guest / none). */
export function offlineOwnerUserId(): string | null {
  return ownerSubject(facts.offlineOwner)
}

export function canEditNow(): boolean {
  return snapshot.allowed.edit
}

/** Test seam. */
export function __setIdentityForTests(patch: Partial<typeof facts>, clock?: () => number): void {
  if (clock) now = clock
  facts = { ...facts, loaded: true, ...patch }
  publish()
}

export function __resetIdentityForTests(): void {
  if (nearExpiryTimer) clearTimeout(nearExpiryTimer)
  nearExpiryTimer = null
  now = () => Date.now()
  facts = { loaded: false, offlineOwner: null, sessionSubject: null, tokenExpiryMs: null, refreshPending: false, terminalRejection: false }
  snapshot = build()
  listeners.clear()
  emitListeners.clear()
}
